import { Building2 } from 'lucide-react';
import { useLocation } from 'wouter';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface DirectorRoleCardProps {
  director: {
    id: string;
    accountId: string;
    accountName: string;
    entityType: string;
    role: 'director';
    appointmentDate: string | null;
    ceasedDate: string | null;
    isActive: boolean;
  };
}

export default function DirectorRoleCard({ director }: DirectorRoleCardProps) {
  const [, navigate] = useLocation();

  const isCeased = !director.isActive || director.ceasedDate !== null;

  return (
    <Card
      className={cn(
        'border-l-4',
        isCeased ? 'border-l-muted opacity-60' : 'border-l-blue-500'
      )}
    >
      <CardContent className="p-3">
        <div className="flex justify-between items-start">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className={cn(
                'font-medium text-sm',
                isCeased && 'line-through'
              )}>
                {director.accountName}
              </span>
              {isCeased && (
                <Badge variant="secondary" className="text-xs">Ceased</Badge>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Director • {director.entityType.replace('_', ' ')}
            </p>

            {director.appointmentDate && (
              <p className="text-xs text-muted-foreground">
                Appointed: {format(new Date(director.appointmentDate), 'dd MMM yyyy')}
              </p>
            )}

            {director.ceasedDate && (
              <p className="text-xs text-muted-foreground">
                Ceased: {format(new Date(director.ceasedDate), 'dd MMM yyyy')}
              </p>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/accounts/${director.accountId}`)}
          >
            View Account
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
